import { useLogout } from "@/hooks/auth-queries";
import {
	BankOutlined,
	EnvironmentOutlined,
	EyeOutlined,
	FormOutlined,
	LogoutOutlined,
	MenuOutlined,
	RocketOutlined,
	ScanOutlined,
	SettingOutlined,
	UserOutlined,
	WalletOutlined,
	WifiOutlined,
} from "@ant-design/icons";
import logo from "@assets/img/FacilityAI_Logo.png";
import type { MenuProps } from "antd";
import { Button, Dropdown, Layout, Space, Tooltip, Typography, message } from "antd";
import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "./Navbar.scss";
import { DB_ORG } from "@/constants/db";
import { useUserStore } from "@/stores";
import { useUIModeStore } from "@/stores/ui-mode-store";
import { GlobalChat } from "@/views/SignedIn/Dashboard/DashboardChat/DashboardChat";

const { Header } = Layout;
const { Text } = Typography;

interface MenuItem {
	key: string;
	icon: ReactNode;
	label: string;
	path: string;
}

type MenuItemType = Required<MenuProps>["items"][number];

const Navbar: FC = () => {
	const [isOffline, setIsOffline] = useState(false);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [spacerHeight, setSpacerHeight] = useState(160);

	const updateSpacerHeight = useCallback(() => {
		if (wrapperRef.current) {
			setSpacerHeight(wrapperRef.current.offsetHeight);
		}
	}, []);

	useEffect(() => {
		updateSpacerHeight();
		const observer = new ResizeObserver(updateSpacerHeight);
		if (wrapperRef.current) observer.observe(wrapperRef.current);
		return () => observer.disconnect();
	}, [updateSpacerHeight]);
	const logout = useLogout();
	const location = useLocation();
	const navigate = useNavigate();
	const { user } = useUserStore();
	const { setMode } = useUIModeStore();

	const handleSwitchToCommandCenter = () => {
		setMode("command-center");
		navigate("/");
	};

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const handleOnline = () => setIsOffline(false);
		const handleOffline = () => setIsOffline(true);

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const firebaseConfig = JSON.parse(
		import.meta.env.VITE_REACT_APP_FIREBASE_CONFIG,
	);
	const isStaging =
		firebaseConfig.projectId.includes("staging") ||
		import.meta.env.VITE_ENV === "staging";

	const handleLogout = async () => {
		try {
			const result = await logout();
			if (result.success) {
				message.success("Logged out successfully");
			} else {
				message.error(result.error || "Failed to logout");
			}
		} catch (error) {
			console.error("Error during logout:", error);
			message.error("An error occurred during logout");
		}
	};

	const menuItems: MenuItem[] = [
		{ key: "dashboard", icon: <BankOutlined />, label: "Dashboard", path: "/" },
		{
			key: "profile",
			icon: <UserOutlined />,
			label: "My Profile",
			path: "/profile",
		},
		{
			key: "locations",
			icon: <EnvironmentOutlined />,
			label: "Locations",
			path: "/locations",
		},
		{
			key: "forms",
			icon: <EyeOutlined />,
			label: "Vision Capture",
			path: "/forms",
		},
		...(user?.hasPlusSubscription ||
		user?.is_trial ||
		user?.isTrial ||
		user?.isTrialAccount
			? [
					{
						key: "asset-scanner",
						icon: <ScanOutlined />,
						label: "Asset Scanner",
						path: "/asset-scanner",
					},
				]
			: []),
		...(user?.organization?.external_firebase_id !== DB_ORG.CUSHMANWAKEFIELD &&
		(user?.hasPlusSubscription ||
			user?.is_trial ||
			user?.isTrial ||
			user?.isTrialAccount)
			? [
					{
						key: "predictive-spend",
						icon: <WalletOutlined />,
						label: "Predictive Spend",
						path: "/predictive-spend",
					},
				]
			: []),
		{
			key: "admin",
			icon: <SettingOutlined />,
			label: "Administration",
			path: "/admin",
		},
	];

	const menuProps: MenuProps = {
		selectedKeys: [location.pathname],
		items: [
			...menuItems.map((item) => ({
				key: item.path,
				icon: item.icon,
				label: <NavLink to={item.path}>{item.label}</NavLink>,
			})),
			{
				type: "divider",
			},
			{
				key: "logout",
				icon: <LogoutOutlined />,
				label: (
					<span
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleLogout();
							}
						}}
						onClick={handleLogout}
					>
						Log Out
					</span>
				),
				danger: true,
			},
		] as MenuItemType[],
	};

	return (
		<>
			<div className="navbar-wrapper" ref={wrapperRef}>
				<Header className="navbar">
					<div className="navbar-content">
						<div className="navbar-left">
							<NavLink to="/" className="logo-link">
								<img src={logo} alt="FacilityAI Logo" className="logo" />
							</NavLink>
						</div>

						<div className="navbar-right">
							<Tooltip title="Switch to AI-powered Command Center" placement="bottom">
								<button
									type="button"
									className="command-center-switcher"
									onClick={handleSwitchToCommandCenter}
								>
									<RocketOutlined className="switcher-icon" />
									<span className="switcher-label">Command Center</span>
								</button>
							</Tooltip>

							{isMobile && (
								<Dropdown
									menu={menuProps}
									trigger={["click"]}
									placement="bottomRight"
								>
									<Button
										color="primary"
										type="text"
										icon={<MenuOutlined />}
										className="menu-button"
									/>
								</Dropdown>
							)}
						</div>
					</div>
				</Header>

				<GlobalChat />
			</div>
			<div style={{ height: spacerHeight }} />

			{isStaging && (
				<div className="staging-banner mb-2">
					<Text strong>STAGING ENVIRONMENT</Text>
				</div>
			)}

			{isOffline && (
				<div className="offline-banner">
					<Space>
						<WifiOutlined />
						<Text>Connection Lost!</Text>
					</Space>
				</div>
			)}
		</>
	);
};

export { Navbar };
